import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import CertificationProgress from './components/CertificationProgress';
import PlatformAnalytics from './components/PlatformAnalytics';
import PracticeChecklist from './components/PracticeChecklist';
import NGOVerification from './components/NGOVerification';
import DocumentationUpload from './components/DocumentationUpload';
import TrainingVideos from './components/TrainingVideos';

const BusinessCertification = () => {
  const [certificationData, setCertificationData] = useState({
    currentLevel: 'Bronce',
    progress: 75,
    nextLevel: 'Plata',
    completedSteps: 9,
    totalSteps: 12
  });

  const [analyticsData, setAnalyticsData] = useState({
    wasteReduction: 85,
    participationRate: 92,
    customerSatisfaction: 88,
    totalBagsSold: 247,
    co2Saved: 156,
    waterSaved: 2340
  });

  const [checklistData, setChecklistData] = useState({
    sections: [
      {
        id: 'food-safety',
        type: 'food-safety',
        title: 'Seguridad Alimentaria',
        description: 'Protocolos de manipulación y almacenamiento',
        notes: `Todos los alimentos deben mantenerse a temperaturas seguras y seguir las normas NOM-251-SSA1-2009 para prácticas de higiene en el proceso de alimentos.`,
        items: [
          {
            id: 'fs1',
            title: 'Certificación HACCP vigente',
            description: 'Sistema de análisis de peligros y puntos críticos de control',
            completed: true,
            required: true
          },
          {
            id: 'fs2',
            title: 'Capacitación del personal en manipulación de alimentos',
            description: 'Todo el personal debe tener certificación actualizada',
            completed: true,
            required: true
          },
          {
            id: 'fs3',
            title: 'Registro de temperaturas diario',
            description: 'Monitoreo continuo de equipos de refrigeración',
            completed: false,
            required: true
          },
          {
            id: 'fs4',
            title: 'Procedimientos de limpieza documentados',
            description: 'Protocolos escritos y verificables de sanitización',
            completed: true,
            required: false
          }
        ]
      },
      {
        id: 'cold-chain',
        type: 'cold-chain',
        title: 'Cadena de Frío',
        description: 'Mantenimiento de temperatura en productos refrigerados',
        notes: `La cadena de frío debe mantenerse ininterrumpida desde la recepción hasta la entrega al consumidor final.`,
        items: [
          {
            id: 'cc1',
            title: 'Equipos de refrigeración calibrados',
            description: 'Termómetros verificados y certificados',
            completed: true,
            required: true
          },
          {
            id: 'cc2',
            title: 'Protocolo de emergencia por corte de energía',
            description: 'Plan de contingencia documentado',
            completed: false,
            required: true
          },
          {
            id: 'cc3',
            title: 'Registro de incidencias de temperatura',
            description: 'Bitácora de eventos fuera de rango',
            completed: true,
            required: false
          }
        ]
      },
      {
        id: 'quality',
        type: 'quality',
        title: 'Estándares de Calidad',
        description: 'Control de calidad de productos y servicios',
        items: [
          {
            id: 'q1',
            title: 'Inspección visual de productos',
            description: 'Verificación de integridad del empaque',
            completed: true,
            required: true
          },
          {
            id: 'q2',
            title: 'Política de productos no abiertos',
            description: 'Solo productos con empaque original intacto',
            completed: true,
            required: true
          },
          {
            id: 'q3',
            title: 'Sistema de trazabilidad',
            description: 'Seguimiento de origen y fechas de productos',
            completed: false,
            required: false
          }
        ]
      },
      {
        id: 'operations',
        type: 'operations',
        title: 'Mejores Prácticas Operativas',
        description: 'Procedimientos operativos estándar',
        items: [
          {
            id: 'o1',
            title: 'Horarios de publicación consistentes',
            description: 'Regularidad en la oferta de bolsas sorpresa',
            completed: true,
            required: false
          },
          {
            id: 'o2',
            title: 'Tiempo de espera máximo 3 minutos',
            description: 'Política de "listo al llegar" implementada',
            completed: false,
            required: true
          },
          {
            id: 'o3',
            title: 'Sistema de códigos QR funcional',
            description: 'Generación y validación de códigos operativa',
            completed: true,
            required: true
          }
        ]
      }
    ]
  });

  const [verificationData, setVerificationData] = useState({
    currentStatus: 'scheduled',
    ngoPartner: 'EcoMéxico Verificaciones',
    nextInspection: '2024-11-15T10:00:00',
    canSchedule: false,
    inspectionHistory: [
      {
        id: 'INS001',
        date: '2024-10-15T14:30:00',
        inspector: 'María González - EcoMéxico',
        status: 'completed',
        score: 87,
        notes: `Excelente implementación de protocolos de seguridad alimentaria. Se observó cumplimiento consistente con las normas mexicanas de higiene. El personal demuestra conocimiento adecuado de los procedimientos.`,
        recommendations: [
          'Mejorar el registro de temperaturas en horarios nocturnos',
          'Implementar sistema digital de trazabilidad',
          'Capacitar personal adicional en procedimientos de emergencia'
        ]
      },
      {
        id: 'INS002',
        date: '2024-09-20T11:00:00',
        inspector: 'Carlos Ruiz - EcoMéxico',
        status: 'completed',
        score: 82,
        notes: `Buena adherencia a protocolos básicos. Se identificaron áreas de mejora en documentación y algunos procedimientos operativos.`,
        recommendations: [
          'Documentar mejor los procedimientos de limpieza',
          'Establecer horarios más consistentes de publicación'
        ]
      }
    ],
    availableSlots: [
      {
        id: 'slot1',
        datetime: '2024-11-20T09:00:00',
        inspector: 'Ana Martínez - EcoMéxico'
      },
      {
        id: 'slot2',
        datetime: '2024-11-22T14:00:00',
        inspector: 'Luis Hernández - EcoMéxico'
      },
      {
        id: 'slot3',
        datetime: '2024-11-25T10:30:00',
        inspector: 'María González - EcoMéxico'
      }
    ]
  });

  const [documentsData, setDocumentsData] = useState({
    categories: [
      {
        id: 'certificates',
        type: 'certificate',
        title: 'Certificaciones Oficiales',
        description: 'Certificados gubernamentales y de organismos reconocidos',
        required: true,
        documents: [
          {
            id: 'doc1',
            name: 'Certificado HACCP 2024',
            size: 2456789,
            uploadDate: '2024-01-15T00:00:00',
            expiryDate: '2024-12-31T00:00:00',
            status: 'approved'
          },
          {
            id: 'doc2',
            name: 'Licencia Sanitaria Municipal',
            size: 1234567,
            uploadDate: '2024-02-01T00:00:00',
            expiryDate: '2025-01-31T00:00:00',
            status: 'approved'
          }
        ]
      },
      {
        id: 'training',
        type: 'training',
        title: 'Certificados de Capacitación',
        description: 'Evidencia de formación del personal',
        required: true,
        documents: [
          {
            id: 'doc3',
            name: 'Capacitación Manipulación Alimentos - Equipo',
            size: 987654,
            uploadDate: '2024-03-10T00:00:00',
            status: 'pending'
          }
        ]
      },
      {
        id: 'compliance',
        type: 'compliance',
        title: 'Cumplimiento Regulatorio',
        description: 'Documentos de cumplimiento con normativas mexicanas',
        required: true,
        documents: [
          {
            id: 'doc4',
            name: 'Aviso de Funcionamiento COFEPRIS',
            size: 1876543,
            uploadDate: '2024-01-20T00:00:00',
            status: 'approved'
          }
        ]
      },
      {
        id: 'photos',
        type: 'photo',
        title: 'Evidencia Fotográfica',
        description: 'Fotos de instalaciones y procedimientos',
        required: false,
        documents: []
      }
    ]
  });

  const [trainingData, setTrainingData] = useState({
    modules: [
      {
        id: 'module1',
        title: 'Fundamentos de Certificación Zero Waste',
        description: 'Conceptos básicos y beneficios del programa',
        videos: [
          {
            id: 'v1',
            title: 'Introducción a Zero Waste',
            description: 'Qué significa ser un negocio Zero Waste certificado',
            duration: 180,
            difficulty: 'beginner',
            completed: true,
            keyPoints: [
              'Definición de Zero Waste en el contexto empresarial',
              'Beneficios económicos y ambientales',
              'Requisitos básicos para la certificación'
            ]
          },
          {
            id: 'v2',
            title: 'Impacto Ambiental de los Residuos Alimentarios',
            description: 'Cómo los residuos afectan el medio ambiente',
            duration: 165,
            difficulty: 'beginner',
            completed: true,
            keyPoints: [
              'Estadísticas de desperdicio alimentario en México',
              'Emisiones de CO₂ por residuos orgánicos',
              'Impacto en recursos hídricos'
            ]
          }
        ]
      },
      {
        id: 'module2',
        title: 'Seguridad Alimentaria y Normativas',
        description: 'Cumplimiento de regulaciones mexicanas',
        videos: [
          {
            id: 'v3',
            title: 'NOM-251-SSA1-2009: Prácticas de Higiene',
            description: 'Norma oficial mexicana para higiene alimentaria',
            duration: 195,
            difficulty: 'intermediate',
            completed: false,
            keyPoints: [
              'Requisitos de la norma oficial',
              'Implementación práctica en negocios',
              'Documentación requerida'
            ]
          },
          {
            id: 'v4',
            title: 'Sistema HACCP Simplificado',
            description: 'Análisis de peligros y puntos críticos de control',
            duration: 210,
            difficulty: 'intermediate',
            completed: false,
            keyPoints: [
              'Identificación de puntos críticos',
              'Monitoreo y registro',
              'Acciones correctivas'
            ]
          }
        ]
      },
      {
        id: 'module3',
        title: 'Operaciones Eficientes',
        description: 'Mejores prácticas para la plataforma FoodRescue',
        videos: [
          {
            id: 'v5',
            title: 'Publicación Rápida de Bolsas Sorpresa',
            description: 'Cómo usar el sistema de publicación en ≤15 segundos',
            duration: 120,
            difficulty: 'beginner',
            completed: true,
            keyPoints: [
              'Flujo optimizado de publicación',
              'Fotografía efectiva de productos',
              'Precios y descripciones atractivas'
            ]
          },
          {
            id: 'v6',
            title: 'Gestión de Códigos QR y Recogidas',
            description: 'Sistema de verificación y política de 3 minutos',
            duration: 150,
            difficulty: 'intermediate',
            completed: false,
            keyPoints: [
              'Generación automática de códigos QR',
              'Proceso de validación en recogida',
              'Manejo de retrasos y compensaciones'
            ]
          }
        ]
      }
    ]
  });

  const handleUpdateChecklist = (sectionId, itemId, completed) => {
    setChecklistData(prev => ({
      ...prev,
      sections: prev?.sections?.map(section =>
        section?.id === sectionId
          ? {
              ...section,
              items: section?.items?.map(item =>
                item?.id === itemId ? { ...item, completed } : item
              )
            }
          : section
      )
    }));
  };

  const handleScheduleInspection = (slotId) => {
    console.log('Scheduling inspection for slot:', slotId);
    // Handle inspection scheduling
  };

  const handleUploadDocument = (file, fileId) => {
    console.log('Uploading document:', file?.name, fileId);
    // Handle document upload
  };

  const handleDeleteDocument = (documentId) => {
    console.log('Deleting document:', documentId);
    // Handle document deletion
  };

  const handleCompleteVideo = (videoId) => {
    setTrainingData(prev => ({
      ...prev,
      modules: prev?.modules?.map(module => ({
        ...module,
        videos: module.videos?.map(video =>
          video?.id === videoId ? { ...video, completed: true } : video
        )
      }))
    }));
  };

  useEffect(() => {
    // Simulate real-time updates
    const interval = setInterval(() => {
      setAnalyticsData(prev => ({
        ...prev,
        totalBagsSold: prev?.totalBagsSold + Math.floor(Math.random() * 2),
        co2Saved: prev?.co2Saved + Math.floor(Math.random() * 3),
        waterSaved: prev?.waterSaved + Math.floor(Math.random() * 10)
      }));
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Helmet>
        <title>Certificación Empresarial - FoodRescue</title>
        <meta name="description" content="Gestiona tu certificación Zero Waste y mejora el ranking de tu negocio en FoodRescue" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Certificación Zero Waste
              </h1>
              <p className="text-muted-foreground">
                Avanza hacia la certificación Plata y mejora tu posicionamiento en la plataforma
              </p>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-8">
                <CertificationProgress certificationData={certificationData} />
                <PlatformAnalytics analyticsData={analyticsData} />
                <NGOVerification 
                  verificationData={verificationData}
                  onScheduleInspection={handleScheduleInspection}
                />
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                <PracticeChecklist 
                  checklistData={checklistData}
                  onUpdateChecklist={handleUpdateChecklist}
                />
                <DocumentationUpload 
                  documentsData={documentsData}
                  onUploadDocument={handleUploadDocument}
                  onDeleteDocument={handleDeleteDocument}
                />
              </div>
            </div>

            {/* Full Width Section */}
            <div className="mt-8">
              <TrainingVideos 
                trainingData={trainingData}
                onCompleteVideo={handleCompleteVideo}
              />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default BusinessCertification;